import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import axios from "axios";
import {
  Alert,
  Box,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  paymentStyles,
  PAYMENT_ENDPOINTS,
  PAYMENT_METHODS,
} from "./payment.constants";
import { displayRazorpay } from "./razorpay";
import { displayHdfcPayment } from "./hdfc";
import { displayPaytm } from "./paytm";
import { is } from "../../../util/is";
import { useDomainConfig } from "../../../hooks/useDomainConfig";
import { formatNumber } from "../../../util/Display";

const paymentFormSchema = Yup.object().shape(
  {
    emailOrPhone: Yup.string()
      .required("Please enter your email or phone number")
      .test("email-or-phone", "Enter a valid email or phone number", (value) => {
        if (!value) return false;
        const isEmail = Yup.string().email().isValidSync(value);
        const isPhone = /^\d{10}$/.test(value);
        return isEmail || isPhone;
      }),
    amount: Yup.number()
      .required()
      .positive()
      .typeError("Amount must be integer")
      .test("is-decimal", "Please enter valid amount", (value) =>
        (value + "").match(/^\d+(\.\d{1,2})?$/)
      ),
    //     .when("minutes", (minutes, schema) => {
    //       return minutes > 0 ? schema.optional() : schema;
    //     }),
    //   minutes: Yup.number()
    //     .positive()
    //     .typeError("Minutes must be integer")
    //     .when("amount", (amount, schema) => {
    //       return amount > 0 ? schema.optional() : schema;
    //     }),
  },
  ["amount"]
);

export const PaymentForm = ({
  connector,
  chargerId,
  clientHandler,
  companyId,
  email,
  domain,
  customerName,
  submitHandler,
  paymentMethod,
}) => {
  const [error, setError] = useState(false);
  const firstInputRef = useRef();
  const [paymentId, setPaymentId] = useState("");
  const domainConfig = useDomainConfig();
  const paymentFormFormik = useFormik({
    initialValues: {
      emailOrPhone: email,
      customerName: customerName,
      amount: 0,
      minutes: 0,
      kwh: 0,
    },
    validationSchema: paymentFormSchema,
    onSubmit: (formData, { setSubmitting, resetForm }) => {
      (async function () {
        setError(false);

        try {
          if (!paymentMethod.id || !paymentMethod.keyId)
            throw new Error("Payment Id or keyId is not present");

          const paymentConfig = getPaymentConfig(paymentMethod);

          if (!paymentConfig) throw new Error("Payment Method not Supported");

          const { emailOrPhone } = formData;
          const isMobile = /^\d{10}$/.test(emailOrPhone);
          const email = !isMobile ? emailOrPhone : "";
          const mobile = isMobile ? emailOrPhone : "";

          // Prepare request data based on payment method
          let requestData = {
            ...formData,
            paymentId,
            chargerId,
            email,
            customerName,
            companyId,
            phone: mobile,
            domain,
            connectorId: !is.empty(connector?.ocpi_connector_id)
              ? connector?.ocpi_connector_id
              : connector?.connector_id,
          };

          // HDFC requires specific fields and amount in Rs only
          if (paymentConfig.method === "hdfc") {
            requestData = {
              email: email, // Send email only if it was actually entered
              phone: mobile, // Send phone only if it was actually entered
              amount: formData.amount, // Amount in Rs only for HDFC
              chargerId,
              connectorId: !is.empty(connector?.ocpi_connector_id)
                ? connector?.ocpi_connector_id
                : connector?.connector_id,
              companyId,
              country: domainConfig.countryCode || "IN",
              domain,
              return_url: `${PAYMENT_ENDPOINTS.getHdfcReturnUrl}?url=${encodeURIComponent(`${window.location.origin}/payment/status`)}`,
            };
          } else if (paymentConfig.method === "paytm") {
            // Paytm requires explicit email and phone fields
            requestData = {
              email: email, // Send email only if it was actually entered
              phone: mobile, // Send phone only if it was actually entered
              amount: formData.amount,
              chargerId,
              connectorId: !is.empty(connector?.ocpi_connector_id)
                ? connector?.ocpi_connector_id
                : connector?.connector_id,
              companyId,
              domain,
              countryCode: domainConfig.countryCode,
              currency: connector?.currency,
            };
          } else {
            // For other payment methods, include currency and countryCode
            requestData.countryCode = domainConfig.countryCode;
            requestData.currency = connector?.currency;
          }

          const response = await axios(paymentConfig.url, {
            method: "post",
            data: requestData,
          });

          if (paymentConfig.method == "razorpay") {
            if (response?.data?.id) {
              displayRazorpay(
                {
                  ...formData,
                  currency: connector?.currency,
                  orderId: response?.data?.id,
                  connectorId: !is.empty(connector?.ocpi_connector_id)
                    ? connector?.ocpi_connector_id
                    : connector.connector_id,
                  chargerId,
                  companyId,
                  keyId: paymentConfig.keyId,
                },
                submitHandler,
              );
            } else throw new Error("Error initating razorpay Payment");
          } else if (paymentConfig.method == "stripe") {
            if (response?.data?.status === "requires_payment_method") {
              clientHandler(true);
            } else if (response?.data?.clientId && response?.data?.paymentId) {
              clientHandler(true, response.data.clientId);
              setPaymentId(response.data.paymentId);
            } else {
              throw new Error("Error initating stripe Payment");
            }
          } else if (paymentConfig.method == "hdfc") {
            // HDFC payment handling
            // HDFC returns payment_links with web, mobile, and iframe URLs
            const hdfcResponse = response?.data;

            if (hdfcResponse?.payment_links) {
              // Prefer web URL for web UI, fallback to mobile or iframe
              const paymentUrl =
                hdfcResponse.payment_links.web ||
                hdfcResponse.payment_links.mobile ||
                hdfcResponse.payment_links.iframe;

              if (paymentUrl && hdfcResponse.id) {
                // Open HDFC payment in popup (like Razorpay)
                displayHdfcPayment({
                  paymentUrl,
                  orderId: hdfcResponse.id,
                  chargerId,
                  connectorId: !is.empty(connector?.ocpi_connector_id)
                    ? connector?.ocpi_connector_id
                    : connector?.connector_id,
                  companyId,
                  submitHandler,
                });
              } else {
                console.error("HDFC Payment Response Error");
                throw new Error(
                  "Error initiating HDFC Payment: Payment link or order ID not found in response",
                );
              }
            } else if (hdfcResponse?.success) {
              // If payment is already successful (webhook processed)
              submitHandler({
                isDone: true,
                status: true,
                message:
                  "Payment is successful! Please wait, charger will start in a while.",
              });
            } else {
              console.error("HDFC Payment Response Error");
              throw new Error(
                "Error initiating HDFC Payment: Invalid response structure",
              );
            }
          } else if (paymentConfig.method == "paytm") {
            // Paytm payment handling
            const paytmResponse = response?.data;

            if (paytmResponse?.txnToken && paytmResponse?.orderId) {
              // Call displayPaytm with required parameters
              displayPaytm(
                {
                  txnToken: paytmResponse.txnToken,
                  orderId: paytmResponse.orderId,
                  mid: paytmResponse.mid,
                  amount: formData.amount,
                  websiteName: paytmResponse.websiteName || "DEFAULT",
                  email: email || emailOrPhone,
                  phone: mobile || emailOrPhone,
                },
                submitHandler,
              );
            } else {
              console.error("Paytm Payment Response Error");
              throw new Error(
                "Error initiating Paytm Payment: txnToken or orderId not found in response",
              );
            }
          } else {
            throw new Error("PaymentMethod not supported");
          }
          setSubmitting(false);
          resetForm({ values: formData });
        } catch (error) {
          console.error("payment error:", error);
          setSubmitting(false);
          setError(true);
          resetForm();
        }
      })();
    },
  });

  const getPaymentConfig = (paymentMethod) => {
    let config;
    switch (paymentMethod.id) {
      case PAYMENT_METHODS.STRIPE: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getClientIdUrl,
          method: "stripe",
        };
        break;
      }
      case PAYMENT_METHODS.RAZORPAY: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getPaymentOrderUrl,
          method: "razorpay",
        };
        break;
      }
      case PAYMENT_METHODS.HDFC: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getHdfcCreateOrderUrl,
          method: "hdfc",
        };
        break;
      }
      case PAYMENT_METHODS.PAYTM: {
        config = {
          keyId: paymentMethod.keyId,
          url: PAYMENT_ENDPOINTS.getPaytmCreateOrderUrl,
          method: "paytm",
        };
        break;
      }

      default: {
        console.error(paymentMethod.id, " Not found");
      }
    }

    return config;
  };

  const {
    errors,
    touched,
    dirty,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    setValues,
    handleChange,
    values: { amount, emailOrPhone, kwh, minutes },
    isValid,
  } = paymentFormFormik;

  // const getMinutes = useCallback(
  //   (amount) => {
  //     const {
  //       fixedFeeCharging = 0,
  //       fixedFeeStarting = 0,
  //       kw,
  //       priceperKW = 0,
  //       priceperMin = 0,
  //       taxPercent = 0,
  //     } = connector ?? {};

  //     if (Number(priceperKW) === 0 || isNaN(Number(priceperKW))) {
  //       return (amount / priceperMin).toFixed(2).replace(/\.00$/, "");
  //     } else {
  //       const amountForCharging = amount * (1 - Number(taxPercent) / 100) - Number(fixedFeeCharging) - Number(fixedFeeStarting);
  //       const pricePerWatt = priceperKW / 1000;
  //       const totalWattToBeCharged = amountForCharging / pricePerWatt;
  //       const chargePerMin = (kw * 1000) / 60;
  //       setFieldValue(
  //         "kwh",
  //         totalWattToBeCharged > 0
  //           ? Number(totalWattToBeCharged.toFixed(2).replace(/\.00$/, "")) / 1000
  //           : 0
  //       );
  //       return (totalWattToBeCharged / chargePerMin)
  //         .toFixed(2)
  //         .replace(/\.00$/, "");
  //     }
  //   },
  //   [connector]
  // );

  const setValuesByAmount = useCallback(
    (amount) => {
      if (amount > 0) {
        const {
          fixedFeeCharging = 0,
          fixedFeeStarting = 0,
          priceperKW = 0,
          taxPercent = 0,
          transactionPercent = 0,
          priceperMin = 0,
        } = connector || {};

        const transactionFeesGSTTax = Number(transactionPercent) / 100;
        const transactionFeesAndAdditionTax =
          transactionFeesGSTTax * Number(amount);
        const subtractAdditionTax =
          Number(amount) - transactionFeesAndAdditionTax;
        const gstTaxcalculate = 1 + Number(taxPercent) / 100;
        const applyGstTax = subtractAdditionTax / gstTaxcalculate;
        const subtractOtherCharges =
          applyGstTax - Number(fixedFeeCharging) - Number(fixedFeeStarting);
        // Calculate KWH if price per KW is available
        if (priceperKW > 0) {
          const totalKw = subtractOtherCharges / Number(priceperKW);
          setFieldValue(
            "kwh",
            totalKw > 0 ? totalKw.toFixed(3).replace(/\.00$/, "") : 0
          );
        }

        // Calculate Minutes if price per Min is available
        if (priceperMin > 0) {
          const totalMin = subtractOtherCharges / Number(priceperMin);
          setFieldValue(
            "minutes",
            totalMin > 0 ? totalMin.toFixed(2).replace(/\.00$/, "") : 0
          );
        }
      } else {
        setFieldValue("kwh", 0);
        setFieldValue("minutes", 0);
      }
    },
    [connector]
  );

  const setValuesByMin = useCallback(
    (minutes) => {
      const {
        fixedFeeCharging = 0,
        fixedFeeStarting = 0,
        priceperMin,
        taxPercent = 0,
        transactionPercent = 0,
      } = connector || {};

      const originalAmount =
        (Number(priceperMin) * Number(minutes)).toFixed(2) +
        Number(fixedFeeCharging) +
        Number(fixedFeeStarting);
      // const originalAmount = (kwh * 1) + Number(fixedFeeCharging) + Number(fixedFeeStarting);
      const amountBeforePG =
        Number(originalAmount) + (originalAmount * Number(taxPercent)) / 100;
      const amount =
        Number(amountBeforePG) +
        Number((amountBeforePG * Number(transactionPercent)) / 100);

      if (minutes > 0) {
        setFieldValue("amount", amount.toFixed(2).replace(/\.00$/, ""));
      } else {
        setFieldValue("amount", 0);
      }
    },
    [connector]
  );

  const setValuesByKw = useCallback(
    (kwh) => {
      const {
        fixedFeeCharging = 0,
        fixedFeeStarting = 0,
        priceperKW,
        taxPercent = 0,
        transactionPercent = 0,
      } = connector || {};

      const cost = Number(kwh) * Number(priceperKW);
      const addSessionFees =
        cost + Number(fixedFeeCharging) + Number(fixedFeeStarting);
      const calculateGstAmount = 1 + Number(taxPercent) / 100;
      const applyGSTAmount = addSessionFees * calculateGstAmount;
      const onlyGSTTax = applyGSTAmount - addSessionFees;
      const calculateConveniencePercent = Number(transactionPercent) / 100;
      const calculateConvenienceFees =
        applyGSTAmount / (1 - calculateConveniencePercent);
      const onlyConvenienceFees = calculateConvenienceFees - applyGSTAmount;
      const totalCost = addSessionFees + onlyGSTTax + onlyConvenienceFees;

      if (kwh > 0) {
        setFieldValue("amount", totalCost.toFixed(2).replace(/\.00$/, ""));
      } else {
        setFieldValue("amount", 0);
      }
    },
    [connector]
  );

  useEffect(() => {
    clientHandler(false);
  }, [amount]);

  // The email/phone is entered in the EmailComponent above; mirror it into the
  // form so the Pay button validates against it (field itself isn't rendered).
  useEffect(() => {
    setFieldValue("emailOrPhone", email || "");
  }, [email]);

  useEffect(() => {
    // Don't auto-focus the amount field — the email field above gets focus
    // first (handled in EmailComponent) so the user fills email → amount.
    setValues({ amount: 0, emailOrPhone, kwh: "", minutes: "" });
  }, [connector?.connector_id]);

  const _handleChange = (e) => {
    const { name, value } = e.currentTarget;
    setFieldValue(name, value);

    if (name === "minutes") {
      setValuesByMin(+value);
    } else if (name === "amount") {
      setValuesByAmount(+value);
    } else if (name === "kwh") {
      setValuesByKw(+value);
    }
    
    // Explicitly validate after all calculations are batched
    setTimeout(() => {
      paymentFormFormik.validateForm();
    }, 0);
  };

  return (
    <Slide direction="up" in={!!connector}>
      <Box className="tabs-parent" sx={{ ...paymentStyles.tabParent, pt: 0, mt: { xs: "-12px", sm: "-16px" } }}>
        {error && (
          <Alert
            variant="outlined"
            severity="error"
            sx={{ marginBottom: "20px", color: "red" }}
          >
            Some Error Occured! Please try again.
          </Alert>
        )}

        <FormikProvider value={paymentFormFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.25 }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  How much would you like to charge?
                </Typography>
                {connector?.priceType === "min" && connector?.priceperMin ? (
                  <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ whiteSpace: "nowrap" }}>
                    {`${formatNumber(connector.priceperMin * 60, {
                      style: "currency",
                      currency: domainConfig.currency,
                    })}`}
                    /hr
                  </Typography>
                ) : connector?.priceperKW ? (
                  <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ whiteSpace: "nowrap" }}>
                    {`${formatNumber(connector.priceperKW, {
                      style: "currency",
                      currency: domainConfig.currency,
                    })}`}
                    /kWh
                  </Typography>
                ) : null}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  required
                  inputRef={firstInputRef}
                  label="Amount"
                  {...getFieldProps("amount")}
                  error={Boolean(touched.amount && errors.amount)}
                  onChange={_handleChange}
                  type="number"
                  fullWidth
                  sx={{ backgroundColor: "white" }}
                />
                {(connector?.priceperKW || connector?.priceperMin) && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      or
                    </Typography>
                    {connector?.priceperKW ? (
                      <TextField
                        label="Units (kWh)"
                        {...getFieldProps("kwh")}
                        onChange={_handleChange}
                        type="number"
                        fullWidth
                        sx={{ backgroundColor: "white" }}
                      />
                    ) : (
                      <TextField
                        label="Minutes"
                        {...getFieldProps("minutes")}
                        onChange={_handleChange}
                        type="number"
                        fullWidth
                        sx={{ backgroundColor: "white" }}
                      />
                    )}
                  </>
                )}
              </Stack>
              <Typography
                variant="caption"
                color={touched.amount && errors.amount ? "error" : "text.secondary"}
                sx={{ mt: 1, display: "block" }}
              >
                {(touched.amount && errors.amount) ||
                  `*Inclusive of ${domainConfig.taxType || "GST"} and service charges`}
              </Typography>
              {!(paymentMethod?.id && paymentMethod?.keyId) && (
                <Typography variant="caption" color="error" sx={{ mt: 1.5, display: "block" }}>
                  Card payment isn't configured for this charger.
                </Typography>
              )}
              <LoadingButton
                variant="contained"
                loading={isSubmitting}
                disabled={
                  !(isValid && dirty) ||
                  !(paymentMethod?.id && paymentMethod?.keyId)
                }
                type="submit"
                sx={{ marginTop: "20px" }}
              >
                Pay &amp; Charge
              </LoadingButton>
            </Box>
          </Form>
        </FormikProvider>
      </Box>
    </Slide>
  );
};